import { InvalidEntityData, InvalidEntityIssueType, InvalidEntityType } from "@deliverr/replenishment-client";
import { keyBy, values } from "lodash";
import { IntlShape } from "react-intl";
import { createSelector } from "reselect";
import { selectTransferCreate } from "transfers/create/store/TransferCreateSelectors";
import { invalidProductRelatedIssues } from "../../actions/utils/invalidOrderItemUtils";
import { ReplenishmentWarehouseSelectLabels } from "../../replenishment/warehouse-selection/ReplenishmentWarehouseSelectLabels";

export const selectSelectedProductsAndIssuesMap = createSelector([selectTransferCreate], (transferCreate) => {
  const { selectedProducts, replenishmentOrder } = transferCreate;
  if (!selectedProducts) {
    return {};
  }
  const orderInvalidEntities = replenishmentOrder?.invalidEntities ?? [];

  const productsWithIssues = values(selectedProducts).map((product) => {
    const relevantIssue = orderInvalidEntities.find(
      (issue) =>
        issue.invalidEntityId === product.lineItemId &&
        issue.invalidEntityType === InvalidEntityType.ORDER_ITEM &&
        invalidProductRelatedIssues.includes(issue.issueType)
    );
    return {
      ...product,
      isInvalid: !!relevantIssue,
    };
  });

  return keyBy(productsWithIssues, "dsku");
});

export const formatInvalidEntityMessage = (
  issue: InvalidEntityData,
  formatMessage: IntlShape["formatMessage"]
): string => {
  switch (issue.issueType) {
    case InvalidEntityIssueType.PRODUCT_NOT_FOUND:
      return formatMessage(ReplenishmentWarehouseSelectLabels.productNotFoundError);
    case InvalidEntityIssueType.INSUFFICIENT_INVENTORY:
      return formatMessage(ReplenishmentWarehouseSelectLabels.outOfStockProductError);
    case InvalidEntityIssueType.CASE_PACK_NOT_FOUND: {
      const casePackDetails = issue.requestDetails?.[InvalidEntityIssueType.CASE_PACK_NOT_FOUND] as
        | { expectedCasePackQty?: number; dsku?: string }
        | undefined;
      const expectedCasePack = casePackDetails?.expectedCasePackQty;
      return expectedCasePack
        ? formatMessage(ReplenishmentWarehouseSelectLabels.casePackNotFoundError, {
            dsku: casePackDetails?.dsku,
            expectedCasePackQty: expectedCasePack,
          })
        : formatMessage(ReplenishmentWarehouseSelectLabels.casePackNotFoundError, { dsku: casePackDetails?.dsku });
    }
    case InvalidEntityIssueType.MULTIPLE_MATCHING_DSKUS:
      return formatMessage(ReplenishmentWarehouseSelectLabels.multipleMatchingDskusError, {
        dsku: issue.invalidEntityId,
      });
    case InvalidEntityIssueType.LOT_NOT_FOUND:
      return formatMessage(ReplenishmentWarehouseSelectLabels.lotNotFoundError, {
        lotNumber: issue.requestDetails?.[InvalidEntityIssueType.LOT_NOT_FOUND]?.requestedLotId,
      });
    default:
      return formatMessage(ReplenishmentWarehouseSelectLabels.productNotFoundError);
  }
};
